import { IconCheck, IconX, IconAlertCircle, IconLoader2 } from "@tabler/icons-react";

// Default user-friendly error message
const DEFAULT_ERROR_MESSAGE = "Document processing failed. Please retry or contact support if the issue persists.";

// Map technical error patterns to user-friendly messages
const ERROR_PATTERNS: Array<{ pattern: RegExp; message: string }> = [
  {
    pattern: /ImportError|ModuleNotFoundError/i,
    message: "Processing service temporarily unavailable. Please retry later.",
  },
  {
    pattern: /S3DownloadError|S3UploadError|S3.*failed|NoSuchKey|AccessDenied/i,
    message: "Failed to access document storage. Please retry.",
  },
  {
    pattern: /ConnectionError|TimeoutError|ConnectTimeout|ReadTimeout/i,
    message: "Service temporarily unavailable. Please retry.",
  },
  {
    pattern: /Weaviate|connect_to_custom|grpc/i,
    message: "Search service temporarily unavailable. Please retry.",
  },
  {
    pattern: /OpenAI|Azure.*API|rate.?limit|quota/i,
    message: "AI service temporarily unavailable. Please retry later.",
  },
  {
    pattern: /Document.*not found|not in pending state/i,
    message: "Document state conflict. Please refresh and retry.",
  },
  {
    pattern: /File.*too (large|small)|size.*exceeded|validation/i,
    message: "Document validation failed. Please check the file and try again.",
  },
  {
    pattern: /parse|parsing|corrupt|invalid.*format/i,
    message: "Unable to parse document. Please ensure the file is not corrupted.",
  },
  {
    pattern: /memory|OOM|killed/i,
    message: "Processing failed due to resource limits. Please try a smaller document.",
  },
];

/**
 * Convert a technical error message to a user-friendly message.
 */
export const toUserFriendlyError = (rawError?: string | null): string => {
  if (!rawError) return DEFAULT_ERROR_MESSAGE;

  for (const { pattern, message } of ERROR_PATTERNS) {
    if (pattern.test(rawError)) {
      return message;
    }
  }

  return DEFAULT_ERROR_MESSAGE;
};

// Suggestion 1: Consolidate Status Sets with a Map
type StatusCategory = "completed" | "failed" | "processing" | "pending";

const STATUS_MAP = new Map<string, StatusCategory>([
  ["completed", "completed"],
  ["ingestion_failed", "failed"],
  ["celery_failed", "failed"],
  ["airflow_failed", "failed"],
  ["pending_ingestion", "pending"],
  ["started", "processing"],
  ["fetched", "processing"],
  ["celery_scheduled", "processing"],
  ["airflow_scheduled", "processing"],
  ["airflow_processed", "processing"],
  ["chunking", "processing"],
  ["analyzing_images", "processing"],
  ["enriching", "processing"],
  ["embedding", "processing"],
  ["ingesting", "processing"],
]);

export const isTerminalStatus = (status?: string | null): boolean => {
  if (!status) return false;
  const category = STATUS_MAP.get(status);
  return category === "completed" || category === "failed";
};

export const isFailedStatus = (status?: string | null): boolean =>
  !!status && STATUS_MAP.get(status) === "failed";

export const isProcessingStatus = (status?: string | null): boolean =>
  !!status && STATUS_MAP.get(status) === "processing";

// Suggestion 5: Type getStatusColor Return Value
type BadgeVariant = "default" | "secondary" | "destructive";

export const getStatusColor = (status?: string | null): BadgeVariant => {
  if (!status) return "secondary";
  const category = STATUS_MAP.get(status);
  if (category === "completed") return "default";
  if (category === "failed") return "destructive";
  return "secondary";
};

// Suggestion 6: Simplify getStatusIcon with a Map
const STATUS_ICONS: Record<string, React.ReactNode> = {
  completed: <IconCheck className="al-w-4 al-h-4" />,
  ingestion_failed: <IconX className="al-w-4 al-h-4" />,
  celery_failed: <IconX className="al-w-4 al-h-4" />,
  airflow_failed: <IconX className="al-w-4 al-h-4" />,
  pending_ingestion: <IconAlertCircle className="al-w-4 al-h-4" />,
};

export const getStatusIcon = (status?: string | null) => {
  if (!status) return null;
  if (isProcessingStatus(status))
    return <IconLoader2 className="al-w-4 al-h-4 al-animate-spin" />;
  return STATUS_ICONS[status] ?? <IconAlertCircle className="al-w-4 al-h-4" />;
};

// Suggestion 3: Move labels Record Outside getStatusLabel
const STATUS_LABELS: Record<string, string> = {
  pending_ingestion: "Pending",
  celery_scheduled: "Queued",
  airflow_scheduled: "Queued",
  started: "Processing",
  fetched: "Processing",
  airflow_processed: "Processing",
  chunking: "Chunking",
  analyzing_images: "Analyzing Images",
  enriching: "Enriching",
  embedding: "Embedding",
  ingesting: "Ingesting",
  completed: "Completed",
  ingestion_failed: "Failed",
  celery_failed: "Failed",
  airflow_failed: "Failed",
};

export const getStatusLabel = (status?: string | null): string =>
  status ? STATUS_LABELS[status] ?? status : "";

// Suggestion 2: Merge PHASE_ORDER and phaseNames into One Object
const PHASES = {
  chunking: { order: 1, label: "Chunking document" },
  analyzing_images: { order: 2, label: "Analyzing images" },
  enriching: { order: 3, label: "Enriching content" },
  embedding: { order: 4, label: "Generating embeddings" },
  ingesting: { order: 5, label: "Storing data" },
} as const;

const TOTAL_PHASES = Object.keys(PHASES).length;

/**
 * Get phase progress info for a document.
 * Returns current phase number, total phases, phase name, and percentage.
 */
export const getPhaseProgress = (
  status?: string | null
): { currentPhase: number; totalPhases: number; phaseName: string; percentage: number } | null => {
  if (!status || !(status in PHASES)) return null;
  const phase = PHASES[status as keyof typeof PHASES];
  return {
    currentPhase: phase.order,
    totalPhases: TOTAL_PHASES,
    phaseName: phase.label,
    percentage: (phase.order / TOTAL_PHASES) * 100,
  };
};

// Time estimation constants (in minutes per MB)
// Based on observed processing times for knowledge ingestion pipeline
const MINUTES_PER_MB = 2; // ~2 minutes per MB average
const BASE_MINUTES = 3; // Minimum processing time (overhead)
const BUFFER_MULTIPLIER = 1.3; // 30% buffer for "taking longer" threshold

/**
 * Estimate processing time based on file size.
 * Returns estimated minutes.
 */
export const estimateProcessingMinutes = (fileSizeBytes: number): number => {
  const fileSizeMB = fileSizeBytes / (1024 * 1024);
  return Math.ceil(BASE_MINUTES + fileSizeMB * MINUTES_PER_MB);
};

/**
 * Get elapsed time in minutes since processing started.
 */
export const getElapsedMinutes = (startedAt?: string | null): number => {
  if (!startedAt) return 0;
  const startTime = new Date(startedAt).getTime();
  const now = Date.now();
  return Math.floor((now - startTime) / (1000 * 60));
};

// Suggestion 4: Keep verbose format for UX (intentional choice over formatMilliseconds)
/**
 * Format minutes into human-readable string.
 */
export const humanizeMinutes = (minutes: number): string => {
  if (minutes < 1) return "less than a minute";
  if (minutes === 1) return "1 minute";
  if (minutes < 60) return `${minutes} minutes`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours === 1) {
    return remainingMinutes > 0
      ? `1 hour ${remainingMinutes} min`
      : "1 hour";
  }
  return remainingMinutes > 0
    ? `${hours} hours ${remainingMinutes} min`
    : `${hours} hours`;
};

/**
 * Check if processing is taking longer than expected.
 */
export const isExceedingEstimate = (
  fileSizeBytes: number,
  startedAt?: string | null
): boolean => {
  if (!startedAt) return false;
  const estimated = estimateProcessingMinutes(fileSizeBytes);
  const elapsed = getElapsedMinutes(startedAt);
  return elapsed > estimated * BUFFER_MULTIPLIER;
};

/**
 * Get processing time display info for a document.
 */
export const getProcessingTimeInfo = (
  fileSizeBytes: number,
  processingStatus?: string | null,
  processingStartedAt?: string | null
): {
  showTimeInfo: boolean;
  elapsedText: string;
  estimateText: string;
  isExceeding: boolean;
} => {
  const isProcessing = isProcessingStatus(processingStatus);

  if (!isProcessing) {
    return {
      showTimeInfo: false,
      elapsedText: "",
      estimateText: "",
      isExceeding: false,
    };
  }

  const estimatedMinutes = estimateProcessingMinutes(fileSizeBytes);
  const elapsedMinutes = getElapsedMinutes(processingStartedAt);
  const exceeding = isExceedingEstimate(fileSizeBytes, processingStartedAt);

  return {
    showTimeInfo: true,
    elapsedText: processingStartedAt
      ? `Time elapsed: ${humanizeMinutes(elapsedMinutes)}`
      : "Processing...",
    estimateText: `Usually takes ~${humanizeMinutes(estimatedMinutes)}`,
    isExceeding: exceeding,
  };
};
